import cn from 'clsx';
import React, {
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  useState,
  useCallback,
  type ChangeEvent,
  useContext,
  type FC,
  memo,
  forwardRef,
  type ReactNode,
} from 'react';
import { TextField, TextFieldLabelStyle } from '@wix/wix-ui-tpa/cssVars';
import { AppId } from '@wix/cashier-common/dist/src/enums/AppId';
import { type FieldName } from '../../types/CardFieldName';
import { DH } from '../../utils/DataHook';
import { AppStateContext } from '../../contexts/AppState';
import s from './styles.scss';
import { classes } from './styles.st.css';

export interface FieldInputProps
  extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'onChange'> {
  fieldName: FieldName;
  label?: string;
  containerClassName?: string;
  isValid?: boolean;
  isErrorCanBeShown?: boolean;
  isTouched?: boolean;
  iconRight?: ReactNode;
  errorMessage?: string;
  suffix?: ReactNode;
  customInput?: boolean;

  onFocus?(e: any, autoFill?: boolean): void;
  onBlur?(e: any, autoFill?: boolean): void;
  onChangeRaw?(e: ChangeEvent<HTMLInputElement>): void;
  onChange?(value: string): void;
}

export const FieldInput: FC<FieldInputProps> = memo(
  forwardRef((props, ref) => {
    const {
      fieldName,
      isValid,
      isTouched,
      isErrorCanBeShown,
      containerClassName,
      iconRight,
      errorMessage,
      suffix,
      customInput,

      onFocus,
      onBlur,
      onChange,
      onChangeRaw,

      ...restProps
    } = props;

    const [isFocused, setFocused] = useState(false);
    const [autoFill, setAutoFill] = useState(false);
    const { appId, labelsStyle } = useContext(AppStateContext);

    const handleCustomFocus = useCallback(
      (shouldFocus: boolean, e?) => {
        setFocused(shouldFocus);

        if (shouldFocus) {
          onFocus?.(e, autoFill);
        } else {
          onBlur?.(e, autoFill);
        }
      },
      [autoFill, onFocus, onBlur],
    );

    if (!customInput) {
      return (
        <TextField
          {...restProps}
          labelStyle={labelsStyle === 'floating' ? TextFieldLabelStyle.Floating : TextFieldLabelStyle.Regular}
          required={false} // NOTE: this removes '*' symbol in the label
          aria-required={true}
          // @ts-expect-error
          ref={ref}
          className={cn(classes.input, {
            [classes.ecomInput]: appId === AppId.Stores,
          })}
          inputClassName={cn(classes.coreInput, s.coreInput, s.inputAutoFillDetection, {
            [classes.coreInputFloating]: labelsStyle === 'floating',
          })}
          data-hook={DH.Field.Input.Node}
          aria-invalid={isTouched && !isValid && isErrorCanBeShown}
          id={fieldName}
          name={fieldName}
          newErrorMessage
          error={isTouched && !isValid && isErrorCanBeShown}
          errorMessage={errorMessage}
          suffix={suffix}
          onChange={(e) => {
            if (onChangeRaw) {
              onChangeRaw(e);
            }
            if (onChange) {
              onChange(e.target.value);
            }
          }}
          onFocus={(e) => onFocus?.(e, autoFill)}
          onBlur={(e) => onBlur?.(e, autoFill)}
          onAnimationStart={(e) => {
            if (e.animationName === s.onAutoFillStart) {
              setAutoFill(true);
            } else if (e.animationName === s.onAutoFillCancel) {
              setAutoFill(false);
            }
          }}
        />
      );
    }

    return (
      <div
        data-hook={DH.Field.Input.Container}
        className={cn(containerClassName, s.container, {
          [s.error]: isTouched && !isValid,
          [s.focus]: isFocused,
        })}
        data-custom-input={true}
      >
        <input
          {...restProps}
          aria-label={restProps.label}
          data-hook={DH.Field.Input.Node}
          ref={ref}
          id={fieldName}
          className={cn(s.input, s.inputAutoFillDetection)}
          aria-invalid={isTouched && !isValid}
          onFocus={(e) => handleCustomFocus(true, e)}
          onBlur={(e) => handleCustomFocus(false, e)}
          onAnimationStart={(e) => {
            if (e.animationName === s.onAutoFillStart) {
              setAutoFill(true);
            } else if (e.animationName === s.onAutoFillCancel) {
              setAutoFill(false);
            }
          }}
          onChange={(e) => {
            if (onChangeRaw) {
              onChangeRaw(e);
            }

            if (onChange) {
              onChange(e.target.value);
            }
          }}
        />
        {iconRight && <div className={s.iconContainer}>{iconRight}</div>}
      </div>
    );
  }),
);
